import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { 
  Route as RouteIcon, MapPin, Clock, Navigation, Users,
  Search, Calendar, Play, Pause, ChevronRight
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiErrors';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const StaffRoutesPage = () => {
  const { activeBusiness } = useBusiness();
  const { businessId } = useParams();
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStaffList = async () => {
    try {
      // Fetch all field staff (not just those with active shifts)
      const { data } = await api.get('/part3/staff');
      const list = data.staff || data || [];
      setStaffList(list);
      if (list.length > 0) {
        setSelectedStaff(list[0].id || list[0].staff_id);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff list'));
    }
  };

  const fetchRoute = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/part3/tracking/routes/${selectedStaff}`, { params: { date: selectedDate } });
      setRouteData(data);
      setLoading(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load route data'));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffList();
  }, [activeBusiness]);

  useEffect(() => {
    if (selectedStaff) {
      fetchRoute();
    }
  }, [selectedStaff, selectedDate]);

  const routePoints = routeData?.points
    ?.filter(p => p?.lat != null && p?.lng != null)
    .map(p => [p.lat, p.lng]) || [];
  const center = routePoints.length > 0 ? routePoints[0] : [28.6139, 77.2090];
  
  const totalDistance = routeData?.total_distance || 0;
  const totalDuration = routeData?.total_duration || 0;
  const visitsCount = routeData?.visits?.length || 0;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Staff Routes</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">View and replay staff movement history</p>
            </div>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                <Users className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Staff</p>
                <p className="text-[14px] font-semibold text-slate-700">{staffList.length}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                <Navigation className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Distance</p>
                <p className="text-[14px] font-semibold text-blue-600">{totalDistance.toFixed(1)} km</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Duration</p>
                <p className="text-[14px] font-semibold text-emerald-600">{formatDuration(totalDuration)}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-purple-50 flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Visits</p>
                <p className="text-[14px] font-semibold text-purple-600">{visitsCount}</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="w-64 relative z-[1000]">
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select Staff Member" />
                </SelectTrigger>
                <SelectContent className="z-[1001]">
                  {staffList.map(staff => (
                    <SelectItem key={staff.id || staff.staff_id} value={staff.id || staff.staff_id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${getInitialColor(staff.name || staff.full_name)}`}>
                          {getInitials(staff.name || staff.full_name)}
                        </div>
                        {staff.name || staff.full_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Map Area */}
      <div className="flex-1 overflow-hidden px-6 pb-6">
        <div className="max-w-[1600px] mx-auto h-full">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-10 h-10 border-3 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="h-full flex">
                {/* Map */}
                <div className="flex-1 relative">
                  <MapContainer
                    center={center}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {routePoints.length > 0 && (
                      <Polyline 
                        positions={routePoints} 
                        color="#0d9488" 
                        weight={4}
                        opacity={0.8}
                      />
                    )}
                    
                    {routeData?.visits?.filter(v => v?.lat != null && v?.lng != null).map((visit, index) => (
                      <Marker
                        key={index}
                        position={[visit.lat, visit.lng]}
                      >
                        <Popup>
                          <div className="text-sm">
                            <p className="font-semibold">{visit.retailer_name}</p>
                            <p className="text-gray-500">{visit.time}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>

                {/* Timeline Sidebar */}
                <div className="w-80 border-l border-slate-200 overflow-y-auto">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <h3 className="text-[13px] font-semibold text-slate-800">Route Timeline</h3>
                  </div>
                  
                  {routeData?.visits && routeData.visits.length > 0 ? (
                    <div className="p-4 space-y-3">
                      {routeData.visits.map((visit, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-teal-600" />
                            </div>
                            {index < routeData.visits.length - 1 && (
                              <div className="w-0.5 flex-1 bg-slate-200 my-1"></div>
                            )}
                          </div>
                          <div className="flex-1 pb-3">
                            <p className="text-[13px] font-medium text-slate-800">{visit.retailer_name}</p>
                            <p className="text-[11px] text-slate-500">{visit.time}</p>
                            {visit.duration && (
                              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-slate-100 rounded text-[10px] text-slate-600">
                                <Clock className="w-3 h-3" />
                                {visit.duration} mins
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <RouteIcon className="w-6 h-6 text-slate-400" />
                      </div>
                      <p className="text-[13px] text-slate-500 text-center">No visits recorded for this route</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffRoutesPage;
